import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    useMediaQuery,
    Tabs,
    Tab,
    Avatar,
    Badge,
    Skeleton
} from '@mui/material';
import {
    IoPersonCircleOutline,
    IoShieldCheckmarkOutline,
    IoOptionsOutline,
    IoKeyOutline,
    IoTrashOutline,
    IoCardOutline,
} from 'react-icons/io5';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import { useReduxOrg } from '@Hooks/reduxOrgHook/useReduxOrg';
import ProfilePage from '@UserManagementPages/ProfilePage';
import ChangePasswordPage from '@UserManagementPages/ChangePasswordPage';
import DeleteAccountPage from '@UserManagementPages/DeleteAccountPage';
import AccessSettingsPage from '@UserManagementPages/AccessSettingsPage';
import PreferencesPage from '@UserManagementPages/PreferencesPage';
import BillingSettingsPage from '@UserManagementPages/BillingSettingsPage';
import UserInfoService from '@Helpers/URLHelpers/UserInfoService';
import ErrorHandler from '@Root/ErrorHandler';
import SnackbarManager from '@CommonFunctionalDisplay/GlobalSnackbar/SnackbarManager';
import '@Partials/_ComStyles.scss';
import '@Partials/_SimpleStyles.scss';
import '@Partials/_OverrideStyles.scss';
import './SettingsPage.scss';

export default function SettingsPage() {

    const [user, setUser] = useState({});
    const [loadingUser, setLoadingUser] = useState(true);
    const [settingTabView, setSettingTabView] = useState(0);
    const [searchParams] = useSearchParams();
    new UserInfoService();
    const errorHandler = new ErrorHandler();
    const isNarrow = useMediaQuery('(max-width: 1280px)');
    useMediaQuery('(max-width: 640px)');
    const { activeUserId, activeUser } = useReduxActiveUser();
    const { currentOrg, currentUserOrgPermissions } = useReduxOrg();
    const isOwner = !currentOrg || currentOrg.ownerUserId === activeUserId;
    // Billing is visible to org admins in addition to the owner — everyone else is a regular
    // member and shouldn't see or reach the Billing tab.
    const canManageBilling = isOwner || !!currentUserOrgPermissions?.allowAdmin;
    const className = 'settingsPage';

    useEffect(() => {
        renderData();
        // Set tab from query parameter if provided
        const tabParam = searchParams.get('tab');
        if (tabParam !== null) {
            const parsedTab = parseInt(tabParam, 10);
            if (parsedTab === 5 && !canManageBilling) {
                setSettingTabView(0);
            } else {
                setSettingTabView(parsedTab);
            }
        }
    }, [searchParams, canManageBilling]);

    const renderData = async () => {
        try {
            setUser(activeUser);
        } catch (err) {
            errorHandler.handleError(err);
            SnackbarManager.error('Failed to load user data');
        } finally {
            setLoadingUser(false);
        }
    };

    const toggleSettingTabView = (_e, view) => {
        setSettingTabView(view);
    };

    const userName = user?.fullName || user?.userName || 'User';
    const userEmail = user?.email || '';
    const initials = useMemo(() => {
        if (!userName) return 'U';
        const parts = userName.trim().split(/\s+/);
        const first = parts[0]?.[0] || '';
        const last = parts.length > 1 ? parts[parts.length - 1]?.[0] : '';
        return (first + (last || '')).toUpperCase();
    }, [userName]);

    return (
        <div className={className}>
            <div className={className + '__container'}>

                {/* Layout */}
                <div className={className + '__content'}>
                    {/* Sidebar */}
                    <aside className={className + '__sidebar'}>
                        <div className={className + '__profileCard'}>
                            <Badge
                                overlap="circular"
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                badgeContent={<span className={className + '__statusDot'} />}
                            >
                                {loadingUser ? (
                                    <Skeleton variant="circular" width={64} height={64} />
                                ) : user?.photo ? (
                                    <Avatar src={user.photo} sx={{ width: 64, height: 64 }} />
                                ) : (
                                    <Avatar sx={{ width: 64, height: 64, bgcolor: '#2784B9', fontWeight: 700 }}>
                                        {initials}
                                    </Avatar>
                                )}
                            </Badge>
                            <div className={className + '__profileCard__info'}>
                                {loadingUser ? (
                                    <>
                                        <Skeleton width={120} height={20} />
                                        <Skeleton width={150} height={16} />
                                    </>
                                ) : (
                                    <>
                                        <div className={className + '__profileCard__name'}>{userName}</div>
                                        {!!userEmail && <div className={className + '__profileCard__email'}>{userEmail}</div>}
                                    </>
                                )}
                            </div>
                        </div>
                        <Tabs
                            value={settingTabView}
                            onChange={toggleSettingTabView}
                            orientation={!isNarrow ? 'vertical' : 'horizontal'}
                            variant="scrollable"
                            className={className + '__navTabs'}
                            textColor="primary"
                            indicatorColor="primary"
                            aria-label="Settings navigation"
                        >
                            <Tab
                                value={0}
                                icon={<IoOptionsOutline />}
                                iconPosition="start"
                                label="Preferences"
                                className={className + '__navTab ' + (settingTabView === 0 ? 'is-active' : '')}
                            />
                            <Tab
                                value={1}
                                icon={<IoPersonCircleOutline />}
                                iconPosition="start"
                                label="My Profile"
                                className={className + '__navTab ' + (settingTabView === 1 ? 'is-active' : '')}
                            />
                            <Tab
                                value={2}
                                icon={<IoShieldCheckmarkOutline />}
                                iconPosition="start"
                                label="Access Center"
                                className={className + '__navTab ' + (settingTabView === 2 ? 'is-active' : '')}
                            />
                            <Tab
                                value={3}
                                icon={<IoKeyOutline />}
                                iconPosition="start"
                                label="Change Password"
                                className={className + '__navTab ' + (settingTabView === 3 ? 'is-active' : '')}
                            />
                            <Tab
                                value={4}
                                icon={<IoTrashOutline />}
                                iconPosition="start"
                                label="Delete Account"
                                className={className + '__navTab ' + (settingTabView === 4 ? 'is-active' : '')}
                            />
                            {canManageBilling && (
                                <Tab
                                    value={5}
                                    icon={<IoCardOutline />}
                                    iconPosition="start"
                                    label="Billing"
                                    className={className + '__navTab ' + (settingTabView === 5 ? 'is-active' : '')}
                                />
                            )}
                        </Tabs>
                    </aside>

                    {/* Main content */}
                    <main className={className + '__main'}>
                        <div className={className + '__card'}>
                            {settingTabView === 0 && <PreferencesPage />}
                            {settingTabView === 1 && <ProfilePage />}
                            {settingTabView === 2 && <AccessSettingsPage />}
                            {settingTabView === 3 && <ChangePasswordPage />}
                            {settingTabView === 4 && <DeleteAccountPage />}
                            {settingTabView === 5 && canManageBilling && <BillingSettingsPage />}
                        </div>
                    </main>
                </div>
            </div>
        </div>
    );
}
